import { h } from 'preact'
import { useContext, useState } from 'preact/hooks'
import Icon from './Icon'
import Body from '../blocks/Body'
import Container from '../blocks/Container'
import Expandable from '../blocks/Expandable'
import Footer from '../blocks/Footer'
import Heading from '../blocks/Heading'
import P from '../elements/P'
import Strong from '../elements/Strong'
import { Context } from '../context/Context'
import { Actions, Framework, Views, ManagePartners } from '../../enums'
import { ButtonFormat } from '../elements/Button'
import { TCF } from '../../../../cmp/tcf/TCF'
import { AC } from '../../../ac/AC'

const PartnerSettingsView = () => {
  const globalState = useContext(Context)
  const { dispatch, state } = globalState
  const [expand, setExpand] = useState({} as IExpand)

  const handleAccept = () => dispatch({ type: Actions.accept })

  const handleCheckbox = (event: any) => {
    const checked: boolean = event.target.checked
    const framework: string = event.target.dataset.framework
    const name: string = event.target.dataset.name
    const value: string = event.target.value
    return dispatch({
      type: Actions.toggle,
      settings: {
        framework,
        id: parseInt(value),
        property: name,
        status: checked
      }
    })
  }

  const handleExpand = (event: any) => {
    const name: string = event.target.dataset.name
    setExpand((prevExpand) => ({
      ...prevExpand,
      [name]: !prevExpand[name]
    }))
  }

  const handleSaveAndExit = () => dispatch({
    type: Actions.save
  })

  const handleViewChange = (event: any) => {
    const key = 'data-view'
    const view = Views[event.target.attributes[key].value] || Views.default
    return dispatch({ type: Actions.view, view })
  }

  const acPartners = AC.partners
  const { acceptedAcPartners } = state
  const vendors = TCF.tcModel.gvl.vendors
  const vendorIds = Object.keys(vendors)
  const partnerSettingsCopy = state.copy[Views.partnerSettings]
  const manageSettingsCopy = state.copy[Views.manageSettings]

  window.$mediavine.newrelic.addPageAction('gdprModal', { layer: '3' })
  window.$mediavine.newrelic.addPageAction('gdprModal', { cmpLanguage: state.language })

  return (
    <Container color={state.color} view={Views.partnerSettings}>
      <Heading view={Views.partnerSettings}>
        <Heading.Title>
          {partnerSettingsCopy.heading}
        </Heading.Title>
        <Heading.Description>
          {partnerSettingsCopy.description1}
        </Heading.Description>
        <Heading.Button
          color={state.color}
          format={ButtonFormat.primary}
          onClick={handleAccept}
        >
          {partnerSettingsCopy.controls.acceptAll}
        </Heading.Button>
      </Heading>
      <Body view={Views.partnerSettings}>
        <Body.Heading>
          {partnerSettingsCopy.description2}
        </Body.Heading>
        <Body.Group>
          {vendorIds.map((id) => (
            <Expandable>
              <Expandable.Heading collapse={!expand[vendors[id].name]}>
                <Expandable.Title>{vendors[id].name}</Expandable.Title>
                <Expandable.Controls>
                    <Expandable.ToggleGroup>
                      <Expandable.ToggleItem>
                        <Expandable.ToggleText>
                          {partnerSettingsCopy.label.consent}
                        </Expandable.ToggleText>
                        <Expandable.ToggleWrapper>
                          <Expandable.Toggle
                            id={`mediavine-gdpr-vendorConsents-id-${vendors[id].id}`}
                            checked={TCF.tcModel.vendorConsents.has(vendors[id].id)}
                            data-framework={Framework.iab}
                            color={state.color}
                            data-name={ManagePartners.vendorConsents}
                            onChange={handleCheckbox}
                            type="checkbox"
                            value={vendors[id].id}
                          />
                          <Expandable.ToggleLabel
                            htmlFor={`mediavine-gdpr-vendorConsents-id-${vendors[id].id}`}
                          />
                        </Expandable.ToggleWrapper>
                      </Expandable.ToggleItem>
                      { vendors[id].legIntPurposes.length > 0 && (
                      <Expandable.ToggleItem>
                        <Expandable.ToggleText>
                          {partnerSettingsCopy.label.legitimateInterests}
                        </Expandable.ToggleText>
                        <Expandable.ToggleWrapper>
                          <Expandable.Toggle
                            id={`mediavine-gdpr-vendorLegitimateInterests-id-${vendors[id].id}`}
                            checked={TCF.tcModel.vendorLegitimateInterests.has(vendors[id].id)}
                            color={state.color}
                            data-framework={Framework.iab}
                            data-name={ManagePartners.vendorLegitimateInterests}
                            onChange={handleCheckbox}
                            type="checkbox"
                            value={vendors[id].id}
                          />
                          <Expandable.ToggleLabel
                            htmlFor={`mediavine-gdpr-vendorLegitimateInterests-id-${vendors[id].id}`}
                          />
                        </Expandable.ToggleWrapper>
                      </Expandable.ToggleItem>
                      )}
                    </Expandable.ToggleGroup>
                    <Expandable.ToggleItem>
                      <Expandable.Button
                        data-name={vendors[id].name}
                        onClick={handleExpand}
                      >
                        <Icon
                          color={state.color}
                          status={!expand[vendors[id].name]}
                        />
                      </Expandable.Button>
                    </Expandable.ToggleItem>
                </Expandable.Controls>
              </Expandable.Heading>
              <Expandable.Content collapse={!expand[vendors[id].name]}>
                {expand[vendors[id].name] && ([
                  {/* Privacy Poliy Link */},
                  <P>
                    <a href={vendors[id].policyUrl} rel="noopener" target="_blank">Privacy Policy</a>
                  </P>,
                  {/* Purposes */},
                  vendors[id].purposes.length > 0 && (
                    <P><Strong>{manageSettingsCopy.purposes.heading}</Strong></P>
                  ),
                  vendors[id].purposes.map((purpose) => ([
                    <P>{manageSettingsCopy.purposes.content[purpose - 1].title}</P>,
                  ])),
                  {/* Legitimate Interest Purposes */},
                  vendors[id].legIntPurposes.length > 0 && (
                    <P><Strong>{manageSettingsCopy.legIntPurposes.heading}</Strong></P>
                  ),
                  {/* This one is unique, as we're actually listing out specific purposes from our copy */},
                  vendors[id].legIntPurposes.map((legIntPurpose) => ([
                    <P>{manageSettingsCopy.purposes.content[legIntPurpose - 1].title}</P>,
                  ])),
                  {/* Special Purposes */},
                  vendors[id].specialPurposes.length > 0 && (
                    <P><Strong>{manageSettingsCopy.specialPurposes.heading}</Strong></P>
                  ),
                  vendors[id].specialPurposes.map((specialPurpose) => ([
                    <P>{manageSettingsCopy.specialPurposes.content[specialPurpose - 1].title}</P>,
                  ])),
                  {/* Features */},
                  vendors[id].features.length > 0 && (
                    <P><Strong>{manageSettingsCopy.features.heading}</Strong></P>
                  ),
                  vendors[id].features.map((feature) => ([
                    <P>{manageSettingsCopy.features.content[feature - 1].title}</P>,
                  ])),
                  {/* Special Features */},
                  vendors[id].specialFeatures.length > 0 && (
                    <P><Strong>{manageSettingsCopy.specialFeatures.heading}</Strong></P>
                  ),
                  vendors[id].specialFeatures.map((specialFeature) => ([
                    <P>{manageSettingsCopy.specialFeatures.content[specialFeature - 1].title}</P>,
                  ])),
                ])}
              </Expandable.Content>
            </Expandable>
          ))}
        </Body.Group>
        <Body.Heading>
          {partnerSettingsCopy.description3}
        </Body.Heading>
        <Body.Group>
          {acPartners.map((acPartner) => (
            <Expandable>
              <Expandable.Heading collapse={!expand[acPartner.providerName]}>
                <Expandable.Title>{acPartner.providerName}</Expandable.Title>
                <Expandable.Controls>
                    <Expandable.ToggleGroup>
                      <Expandable.ToggleItem>
                        {/* <Expandable.ToggleText>
                          {partnerSettingsCopy.label.consent}
                        </Expandable.ToggleText> */}
                        <Expandable.ToggleWrapper>
                          <Expandable.Toggle
                            id={`mediavine-gdpr-acPartners-id-${acPartner.providerId}`}
                            checked={acceptedAcPartners[acPartner.providerId]}
                            data-framework={Framework.ac}
                            color={state.color}
                            data-name={ManagePartners.additionalConsent}
                            onChange={handleCheckbox}
                            type="checkbox"
                            value={acPartner.providerId}
                          />
                          <Expandable.ToggleLabel
                            htmlFor={`mediavine-gdpr-acPartners-id-${acPartner.providerId}`}
                          />
                        </Expandable.ToggleWrapper>
                      </Expandable.ToggleItem>
                    </Expandable.ToggleGroup>
                    <Expandable.ToggleItem>
                      <Expandable.Button
                        data-name={acPartner.providerName}
                        onClick={handleExpand}
                      >
                        <Icon
                          color={state.color}
                          status={!expand[acPartner.providerName]}
                        />
                      </Expandable.Button>
                    </Expandable.ToggleItem>
                </Expandable.Controls>
              </Expandable.Heading>
              <Expandable.Content collapse={!expand[acPartner.providerName]}>
              {expand[acPartner.providerName] && (
                <P>
                  <a href={acPartner.policyUrl} rel="noopener" target="_blank">Privacy Policy</a>
                </P>
              )}
              </Expandable.Content>
            </Expandable>
          ))}
        </Body.Group>
      </Body>
      <Footer>
        <Footer.Row view={Views.manageSettings}>
          <Footer.Group childPosition="flex-start">
            <Footer.Button
              data-view={Views.default}
              format={ButtonFormat.inverse}
              onClick={handleViewChange}
            >
              {partnerSettingsCopy.controls.goBack}
            </Footer.Button>
          </Footer.Group>
          <Footer.Group childPosition="flex-end">
            <Footer.Button
              color={state.color}
              format={ButtonFormat.secondary}
              onClick={handleSaveAndExit}
            >
              {partnerSettingsCopy.controls.saveAndExit}
            </Footer.Button>
            <Footer.Button
              color={state.color}
              format={ButtonFormat.primary}
              onClick={handleAccept}
            >
              {manageSettingsCopy.controls.acceptAll}
            </Footer.Button>
          </Footer.Group>
        </Footer.Row>
      </Footer>
    </Container>
  )
}

export default PartnerSettingsView

interface IExpand {
  [key: string]: boolean
}
